/**
 * Formats or extracts the accurate Sender Account Number matching the bank's regex masking format.
 * Examples:
 * - SCB / KBANK / BAY: 'x-7588' or 'xxx-7588'
 * - KTB / GSB / GHB: '...7588'
 * - TTB: 'xxx-x-7588-x'
 * - BBL: '3852xxx667'
 */
export function formatSenderAccount({
  rawText,
  senderLast4,
  bankPrefix,
  accountFormatSample,
  accountRegex,
  senderAccountRaw,
}: {
  rawText?: string | null;
  senderLast4?: string | null;
  bankPrefix?: string | null;
  accountFormatSample?: string | null;
  accountRegex?: string | null;
  senderAccountRaw?: string | null;
}): string {
  // 0. If backend already passed raw extracted account token
  if (senderAccountRaw && String(senderAccountRaw).trim()) {
    return String(senderAccountRaw).trim();
  }

  // 1. Try to extract exact account token directly from raw message text
  if (rawText && typeof rawText === 'string') {
    const text = rawText.trim();

    // 1a. Try custom accountRegex if available
    if (accountRegex) {
      try {
        const re = new RegExp(accountRegex, 'i');
        const m = text.match(re);
        if (m) {
          const token = m.slice(1).find((v) => v != null) || m[0];
          if (token && String(token).trim()) {
            return String(token).trim();
          }
        }
      } catch {}
    }

    // 1b. Try explicit 'จาก/from ...' pattern
    const fromMatch = text.match(/(?:จากบัญชี|จาก|from\s*account|from)[^\d\n]{0,40}?([xX*\d.-]*\d{3,4})\b/i);
    if (fromMatch && fromMatch[1] && fromMatch[1].trim()) {
      return fromMatch[1].trim();
    }

    // 1c. Try standard Thai bank masking patterns in raw message
    const m = text.match(/(?:[xX*]+-[xX*]+-)?\d{3,4}-[xX*]+/i)
      || text.match(/\b\d{3,4}[xX*]+\d{3,4}\b/i)
      || text.match(/[xX*]{1,}[-\s]?\d{3,4}\b/i)
      || text.match(/(?:\.{2,})\d{3,4}\b/i)
      || text.match(/(?:บ\/ช|เข้าบ\/ช|โอนเงินไป)\D{0,12}(\d{3,4})\b/i);
    if (m && m[0] && m[0].trim()) {
      return m[0].trim();
    }
  }

  // 2. If no raw match, format digits based on formatSample or bank rules
  if (!senderLast4) return '-';

  const digits = String(senderLast4).replace(/\D/g, '');
  if (!digits) return senderLast4;

  const prefix = String(bankPrefix || '').toUpperCase();
  const format = String(accountFormatSample || '').toLowerCase();

  if (format.includes('...') || prefix === 'KTB' || prefix === 'GSB' || prefix === 'GHB') {
    return `...${digits}`;
  }
  if (format.includes('xxx-x-') || prefix === 'TTB') {
    return `xxx-x-${digits}-x`;
  }
  if (format.includes('xxx-') || prefix === 'BAAC' || prefix === 'UOB') {
    return `xxx-${digits}`;
  }
  if (format.includes('x-') || prefix === 'SCB' || prefix === 'KBANK' || prefix === 'BAY') {
    return `x-${digits}`;
  }

  return `x-${digits}`;
}
